import React, { useEffect, useState, lazy, Suspense } from 'react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Move, ArrowLeft, Building, Calendar, Phone, Mail } from 'lucide-react';
import { fetchProjectDetails, searchProjects } from '../services/api';
import { getWhatsAppLink, WhatsAppMessages } from '../utils/whatsapp';
import { sanitizeHTML } from '../utils/sanitizeHTML';
import { formatPrice } from '../utils/currency';
import { useCurrency } from '../contexts/CurrencyContext';
import Header from '../components/Header';
import { useTranslation } from 'react-i18next';
import SmartImage from '../components/SmartImage';
import WhenVisible from '../components/WhenVisible';

// Leaflet is ~45 KB gzipped plus its stylesheet, for a map far below the
// fold. Loaded on its own, it stays off the path to the first render.
const PropertyMap = lazy(() => import('../components/PropertyMap'));
// The map's own box (PropertyMap renders a 400px-high container), held open
// while the chunk loads so nothing below it jumps.
const MAP_PLACEHOLDER = <div style={{ height: '400px', width: '100%', borderRadius: '12px', background: 'rgba(255,255,255,0.03)' }} />;
import Breadcrumbs from '../components/Breadcrumbs';
import SEO from '../components/SEO';
import { JsonLd, projectSchema, realEstateListingSchema, breadcrumbSchema, projectFaqSchema } from '../components/JsonLd';
import { areaForDistrict, areaName } from '../config/areas';
import { LANGS } from '../config/langs';
import { ui, districtLabel } from '../config/seoCopy';
import {
    normaliseProject,
    projectTitle,
    projectDescription,
    projectAnswer,
    projectLabels,
    projectH1,
    isIndexable,
    compactPrice
} from '../config/projectSeo';
import { projectContent, projectFaq, heroImage } from '../config/projectContent';

const PropertyDetails = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const { currency } = useCurrency();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        const loadDetails = async () => {
            try {
                // The build writes each project's record to /data/project/<id>.json
                // — the same record the static HTML was generated from — so the
                // page reads that first and states exactly what the static page
                // did. A project added since the last build has no snapshot yet
                // and comes from the live API instead.
                const snapshot = await fetch(`/data/project/${encodeURIComponent(id)}.json`)
                    .then((r) => (r.ok && (r.headers.get('content-type') || '').includes('json') ? r.json() : null))
                    .catch(() => null);
                if (snapshot) {
                    setProperty(snapshot);
                    return;
                }
                const response = await fetchProjectDetails(id);
                if (response && response.data) {
                    setProperty(response.data);
                }
            } catch (error) {
                console.error('Failed to load property details:', error);
            } finally {
                setLoading(false);
            }
        };
        loadDetails();
    }, [id]);

    // Sibling projects in the same district — internal links that give
    // Googlebot a path to the pages currently stuck at "Discovered".
    const relatedDistrict = property
        ? normaliseProject(property).district
        : '';
    const relatedLang = LANGS.includes((i18n.language || '').split('-')[0]) ? i18n.language.split('-')[0] : 'ar';
    const snapshotLinks = property?._links || null;
    useEffect(() => {
        if (snapshotLinks) {
            setRelated(snapshotLinks.related.map((r) => ({ id: r.id, title: r.title, priceLabel: compactPrice(r.priceMin, relatedLang) || '' })));
            return;
        }
        if (!relatedDistrict) { setRelated([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await searchProjects({ district_name: relatedDistrict, per_page: 8 });
                const rows = res?.data?.data || res?.data || [];
                if (cancelled || !Array.isArray(rows)) return;
                setRelated(
                    rows
                        .map(normaliseProject)
                        .filter((r) => r.id && String(r.id) !== String(id) && r.title)
                        .slice(0, 6)
                        .map((r) => ({
                            id: r.id,
                            title: r.title,
                            priceLabel: compactPrice(r.priceMin, relatedLang) || ''
                        }))
                );
            } catch {
                // A failed related-projects call must never blank the page.
                if (!cancelled) setRelated([]);
            }
        })();
        return () => { cancelled = true; };
    }, [relatedDistrict, id, relatedLang, snapshotLinks]);

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-dark-bg)',
                color: 'var(--color-gold)',
                fontSize: '1.5rem'
            }}>
                {t('details.loading')}
            </div>
        );
    }

    if (!property) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-dark-bg)',
                color: '#fff',
                gap: '1rem'
            }}>
                <h2>{t('details.not_found')}</h2>
                <Link to="/" className="btn-primary" style={{ textDecoration: 'none' }}>{t('details.back_home')}</Link>
            </div>
        );
    }

    // SEO — every string comes from src/config/projectSeo.js, the same module
    // the build-time prerenderer uses, so the HTML Googlebot fetches and the
    // HTML React renders say exactly the same thing.
    const seoLang = relatedLang;
    const p = normaliseProject(property || {});
    const seoTitle = projectTitle(property, seoLang);
    const seoDesc = projectDescription(property, seoLang);
    const answer = projectAnswer(property, seoLang);
    const faqItems = projectFaq(property || {}, seoLang);
    // Payment plan, units, floor plans, nearby, amenities, gallery — the same
    // model the prerenderer writes from (src/config/projectContent.js).
    const C = projectContent(property || {}, seoLang);
    const T = C.labels;
    // The developer's description arrives in English; say so on other pages.
    const enOnly = seoLang === 'en' ? {} : { lang: 'en', dir: 'ltr' };
    const seoImage = p.image || undefined;
    const hero = heroImage(property || {}, seoLang);
    // The project's area and developer pages, as the build linked them
    // (generated areas included); a project without a snapshot falls back to
    // the curated area list.
    const curatedArea = areaForDistrict(p.district);
    const linkArea = snapshotLinks?.area || (curatedArea && { slug: curatedArea.slug, en: curatedArea.en, ar: curatedArea.ar });
    const linkDev = snapshotLinks?.developer || null;
    const PL = projectLabels(seoLang);
    const crumbItems = [
        { name: ui(seoLang).home, to: '/' },
        { name: PL.projects, to: '/projects' },
        ...(linkArea ? [{ name: areaName(linkArea, seoLang), to: `/area/${linkArea.slug}` }] : []),
        { name: p.title }
    ];

    return (
        <div style={{ minHeight: '100vh' }}>
            {property && (
                <>
                    <SEO
                        fullTitle={seoTitle}
                        description={seoDesc}
                        path={`/project/${id}`}
                        image={seoImage}
                        type="product"
                        // Same rule as the prerendered page's robots tag.
                        noindex={!isIndexable(property)}
                        keywords={`${p.title}, ${p.title} price, ${p.title} payment plan, سعر ${p.title}, مشروع ${p.title}, ${p.developer}, ${p.district}, off plan dubai, عقارات دبي على الخارطة`}
                    />
                    <JsonLd data={projectSchema(property, seoLang)} />
                    <JsonLd data={realEstateListingSchema(property, seoLang)} />
                    <JsonLd data={projectFaqSchema(property, seoLang)} />
                    <JsonLd data={breadcrumbSchema(crumbItems.map((c) => ({ name: c.name, url: c.to || `/project/${id}` })))} />
                </>
            )}
            <Header />

            <style>{`
                .details-hero {
                    width: 100%;
                    height: 500px;
                    position: relative;
                    overflow: hidden;
                    margin-top: 80px;
                }

                @media (max-width: 768px) {
                    .details-hero {
                        height: 300px;
                        margin-top: 70px;
                    }
                }

                .details-grid {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    gap: 3rem;
                    margin-top: -100px;
                    position: relative;
                    z-index: 10;
                    padding-bottom: 4rem;
                }

                @media (max-width: 900px) {
                    .details-grid {
                        grid-template-columns: 1fr;
                        margin-top: -50px;
                        gap: 2rem;
                    }
                }

                .property-title {
                    font-size: 2.5rem;
                    font-weight: 800;
                    color: #fff;
                    margin-bottom: 1rem;
                    line-height: 1.2;
                }

                @media (max-width: 480px) {
                    .property-title {
                        font-size: 1.8rem;
                    }
                }

                .specs-container {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 2rem;
                    text-align: center;
                }

                @media (max-width: 768px) {
                    .specs-container {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 1.5rem;
                    }
                }

                .sticky-sidebar {
                    position: sticky;
                    top: 100px;
                }

                @media (max-width: 900px) {
                    .sticky-sidebar {
                        position: static;
                    }
                }
            `}</style>

            {/* Back Button */}
            <div className="container" style={{ paddingTop: '20px', position: 'absolute', zIndex: 20, top: '80px' }}>
                <Link to="/" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    background: 'rgba(0,0,0,0.5)',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    backdropFilter: 'blur(5px)'
                }}>
                    <ArrowLeft size={20} />
                    {t('details.back_props')}
                </Link>
            </div>

            {/* Hero Image */}
            <div className="details-hero">
                {/* This is the LCP element on a project page — eager, high
                    priority, and never lazy. */}
                <SmartImage
                    src={hero?.src}
                    alt={hero?.alt || property.title}
                    width={1600}
                    height={900}
                    displayWidth={1280}
                    sizes="100vw"
                    priority
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.style.backgroundColor = '#1a1a2e';
                    }}
                />
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(to top, rgba(5,5,16,1), transparent)',
                    height: '200px'
                }} />
            </div>

            {/* Content */}
            <div className="container">
                <div className="details-grid">
                    {/* Left Column */}
                    <div>
                        {/* The same trail as the BreadcrumbList schema above. */}
                        <Breadcrumbs items={crumbItems} />
                        <div style={{ marginBottom: '2rem' }}>
                            <span style={{
                                background: 'var(--color-gold)',
                                color: '#000',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                textTransform: 'uppercase'
                            }}>
                                {property.status || t('details.for_sale')}
                            </span>
                        </div>

                        <h1 className="property-title">
                            {projectH1(property, seoLang)}
                        </h1>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#999',
                            marginBottom: '2rem'
                        }}>
                            <MapPin size={18} color="var(--color-neon-blue)" />
                            {p.location}
                        </div>

                        {/*
                          Direct-answer paragraph — the first prose on the page.

                          Answer engines (AI Overviews, ChatGPT, Perplexity) lift
                          the first self-contained paragraph that fully resolves
                          the query. This one names the project, developer,
                          location, unit mix, price, handover and how to buy, so
                          quoting it alone is a complete answer — and the quote
                          carries our company name with it.

                          It is the same string as the schema `description`, on
                          purpose.
                        */}
                        <div
                            className="project-answer"
                            data-answer="true"
                            style={{
                                padding: '1.5rem 1.75rem',
                                marginBottom: '2rem',
                                background: 'rgba(212, 175, 55, 0.06)',
                                borderInlineStart: '3px solid var(--color-gold)',
                                borderRadius: '8px'
                            }}
                        >
                            <p style={{ color: '#e8e8e8', fontSize: '1.05rem', lineHeight: 1.9, margin: 0 }}>
                                {answer}
                            </p>
                        </div>

                        {/* Specs Grid */}
                        <div className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
                            <div className="specs-container">
                                <div>
                                    <Bed size={28} color="var(--color-gold)" />
                                    <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: '0.5rem 0' }}>
                                        {property.beds || t('common.contact_for_details')}
                                    </p>
                                    <span style={{ color: '#999', fontSize: '0.9rem' }}>{t('details.bedrooms')}</span>
                                </div>
                                <div>
                                    <Bath size={28} color="var(--color-gold)" />
                                    <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: '0.5rem 0' }}>
                                        {property.baths || t('common.contact_for_details')}
                                    </p>
                                    <span style={{ color: '#999', fontSize: '0.9rem' }}>{t('details.bathrooms')}</span>
                                </div>
                                <div>
                                    <Move size={28} color="var(--color-gold)" />
                                    <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: '0.5rem 0' }}>
                                        {property.min_area || t('common.contact_for_details')}
                                    </p>
                                    <span style={{ color: '#999', fontSize: '0.9rem' }}>{t('details.sqft')}</span>
                                </div>
                                <div>
                                    <Building size={28} color="var(--color-gold)" />
                                    <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', margin: '0.5rem 0' }}>
                                        {property.property_category?.[0] || t('common.contact_for_details')}
                                    </p>
                                    <span style={{ color: '#999', fontSize: '0.9rem' }}>{t('details.type')}</span>
                                </div>
                            </div>
                        </div>


                        {/* Up one level: the developer's page and the area's page. */}
                        {(linkDev || linkArea) && (
                            <p style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', margin: '0 0 0.5rem' }}>
                                {linkDev && (
                                    <Link to={`/developer/${linkDev.slug}`} style={{ color: 'var(--color-gold)', textDecoration: 'none' }}>
                                        {PL.developerAll(linkDev.name)}
                                    </Link>
                                )}
                                {linkArea && (
                                    <Link to={`/area/${linkArea.slug}`} style={{ color: 'var(--color-gold)', textDecoration: 'none' }}>
                                        {PL.allInArea(areaName(linkArea, seoLang))}
                                    </Link>
                                )}
                            </p>
                        )}

                        {/* Payment plan — the project's own, from its detail record. */}
                        {C.plan.length > 0 && (
                            <section className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{T.plan}</h2>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                                    {C.plan.map((r) => (
                                        <div key={r.key} style={{ border: '1px solid rgba(255,215,0,0.25)', borderRadius: 10, padding: '1rem' }}>
                                            <p style={{ color: 'var(--color-gold)', fontSize: '1.75rem', fontWeight: 800, margin: 0 }}>{r.percent}%</p>
                                            <p style={{ color: '#bbb', margin: '0.25rem 0 0' }}>{r.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Unit types, sizes and price ranges. */}
                        {C.units.length > 0 && (
                            <section className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{T.units}</h2>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', color: '#ddd' }}>
                                        <thead>
                                            <tr style={{ color: '#999', textAlign: 'start' }}>
                                                <th style={{ textAlign: 'start', padding: '0.5rem' }}>{T.unit}</th>
                                                <th style={{ textAlign: 'start', padding: '0.5rem' }}>{T.size}</th>
                                                <th style={{ textAlign: 'start', padding: '0.5rem' }}>{T.price}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {C.units.map((u) => (
                                                <tr key={`${u.label}-${u.size}-${u.price}`} style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                                    <td style={{ padding: '0.6rem 0.5rem' }}>{u.label}{u.type ? <span style={{ color: '#888' }}> · {u.type}</span> : null}</td>
                                                    <td style={{ padding: '0.6rem 0.5rem' }}>{u.size}</td>
                                                    <td style={{ padding: '0.6rem 0.5rem', color: 'var(--color-gold)' }}>{u.price}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {C.floorPlans.length > 0 && (
                                    <>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', margin: '1.5rem 0 1rem' }}>{T.floorPlans}</h3>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                                            {C.floorPlans.map((fp) => (
                                                <figure key={fp.src} style={{ margin: 0, background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                                                    <SmartImage src={fp.src} alt={fp.alt} width={480} height={360} displayWidth={480}
                                                        sizes="(max-width: 767px) 45vw, 200px"
                                                        style={{ width: '100%', height: 160, objectFit: 'contain' }}
                                                        onError={(e) => { e.target.closest('figure').style.display = 'none'; }} />
                                                    <figcaption style={{ color: '#333', fontSize: '0.8rem', padding: '0.4rem 0.6rem' }}>{fp.alt}</figcaption>
                                                </figure>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </section>
                        )}

                        {/* The developer's description. */}
                        <section className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{T.description}</h2>
                            <div
                                {...enOnly}
                                style={{ color: '#ccc', lineHeight: 1.8 }}
                                dangerouslySetInnerHTML={{ __html: sanitizeHTML(property.description) || t('details.default_description') }}
                            />
                        </section>

                        {C.amenities.length > 0 && (
                            <section className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{T.amenities}</h2>
                                <ul {...enOnly} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                                    {C.amenities.map((a) => (
                                        <li key={a} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999, padding: '0.4rem 0.9rem', color: '#ddd', fontSize: '0.9rem' }}>{a}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Location: the map, and the nearby places with distances. */}
                        <section className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{C.nearby.length ? T.nearby : t('details.location_map')}</h2>
                            {C.nearby.length > 0 && (
                                <ul style={{ listStyle: 'none', margin: '0 0 1.5rem', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem 1.5rem' }}>
                                    {C.nearby.map((n) => (
                                        <li key={n.name} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', color: '#ddd', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0.4rem 0' }}>
                                            <span>{n.name}</span>
                                            <span style={{ color: 'var(--color-gold)', whiteSpace: 'nowrap' }}>{n.distance}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <WhenVisible minHeight="400px">
                                <Suspense fallback={MAP_PLACEHOLDER}>
                                    <PropertyMap property={property} />
                                </Suspense>
                            </WhenVisible>
                        </section>

                        {/* Gallery, with alt text that says what each image shows. */}
                        {C.gallery.length > 0 && (
                            <section className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{T.gallery}</h2>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                                    {C.gallery.map((g) => (
                                        <div key={g.src} style={{ height: '150px', borderRadius: '8px', overflow: 'hidden' }}>
                                            <SmartImage
                                                src={g.src}
                                                alt={g.alt}
                                                width={480}
                                                height={300}
                                                displayWidth={480}
                                                sizes="(max-width: 767px) 45vw, 240px"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                                                onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                                                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/*
                          Visible FAQ, identical text to the FAQPage schema
                          above. Structured data that is not on the page is a
                          rich-results violation, and — more to the point — an
                          answer engine quotes what it can see.
                        */}
                        <section className="glass-card project-faq" style={{ padding: '2rem', marginTop: '2rem' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>{projectLabels(seoLang).faq(p.title)}</h2>
                            {faqItems.map((item) => (
                                <div key={item.q} style={{ marginBottom: '1.5rem' }}>
                                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-gold)', marginBottom: '0.5rem' }}>
                                        {item.q}
                                    </h3>
                                    <p style={{ color: '#ccc', lineHeight: 1.8, margin: 0 }}>{item.a}</p>
                                </div>
                            ))}
                            {['ar', 'en'].includes(seoLang) && (
                                <p style={{ marginTop: '1.5rem', marginBottom: 0 }}>
                                    <Link to="/faq" style={{ color: 'var(--color-neon-blue)', textDecoration: 'none' }}>
                                        {t('details.faq_more')}
                                    </Link>
                                </p>
                            )}
                        </section>

                        {/*
                          Related projects in the same district.

                          Crawl-budget plumbing as much as UX: 1,000+ project
                          URLs sat in "Discovered — currently not indexed"
                          largely because nothing linked to them. Every project
                          page now hands Googlebot a handful of sibling URLs,
                          turning a flat list into a connected graph.
                        */}
                        {related.length > 0 && (
                            <div className="glass-card" style={{ padding: '2rem', marginTop: '2rem' }}>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: '1.25rem' }}>
                                    {snapshotLinks ? PL.similar : PL.related(linkArea ? areaName(linkArea, seoLang) : (districtLabel(p.district) || p.city))}
                                </h2>
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.75rem' }}>
                                    {related.map((r) => (
                                        <li key={r.id}>
                                            <Link
                                                to={`/project/${r.id}`}
                                                style={{
                                                    color: '#ddd',
                                                    textDecoration: 'none',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: '1rem',
                                                    flexWrap: 'wrap',
                                                    paddingBottom: '0.75rem',
                                                    borderBottom: '1px solid rgba(255,255,255,0.08)'
                                                }}
                                            >
                                                <span style={{ fontWeight: 600 }}>{r.title}</span>
                                                <span style={{ color: 'var(--color-gold)', whiteSpace: 'nowrap' }}>
                                                    {r.priceLabel}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                <p style={{ marginTop: '1.25rem', marginBottom: 0 }}>
                                    <Link to="/projects" style={{ color: 'var(--color-neon-blue)', textDecoration: 'none' }}>
                                        {t('home.view_all_projects')}
                                    </Link>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Price & Contact */}
                    <div>
                        <div className="glass-card sticky-sidebar" style={{ padding: '2rem', marginBottom: '2rem' }}>
                            <p style={{ color: '#999', marginBottom: '0.5rem' }}>{t('details.starting_price')}</p>
                            <h2 style={{
                                fontSize: '2.5rem',
                                fontWeight: 800,
                                color: 'var(--color-gold)',
                                marginBottom: '2rem'
                            }}>
                                {(property.price_start || property.min_price)
                                    ? formatPrice(property.price_start || property.min_price, currency)
                                    : t('details.price_request')}
                            </h2>

                            {property.developer_name && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1.5rem',
                                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <Building size={20} color="var(--color-neon-blue)" />
                                    <div>
                                        <p style={{ color: '#999', fontSize: '0.8rem' }}>{t('details.developer')}</p>
                                        <p style={{ color: '#fff', fontWeight: 600 }}>{property.developer_name}</p>
                                    </div>
                                </div>
                            )}

                            {property.handover && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '2rem'
                                }}>
                                    <Calendar size={20} color="var(--color-neon-blue)" />
                                    <div>
                                        <p style={{ color: '#999', fontSize: '0.8rem' }}>{t('details.handover')}</p>
                                        <p style={{ color: '#fff', fontWeight: 600 }}>{property.handover}</p>
                                    </div>
                                </div>
                            )}

                            <a
                                href={getWhatsAppLink(WhatsAppMessages.projectInquiry(property.title, property.location || property.city_name))}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary"
                                style={{
                                    width: '100%',
                                    padding: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    background: '#25D366',
                                    borderColor: '#25D366',
                                    color: '#fff',
                                    textDecoration: 'none'
                                }}>
                                <WhatsAppIcon size={18} color="#fff" />
                                {t('details.whatsapp_contact')}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyDetails;
